import { useEffect, useState } from 'react'
import axios from 'axios'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../helpers/apiRequester'
import { useNavigate } from 'react-router-dom'
import { useSetUserOrganisations, useUpdateAuthUserData, useSetPcpReferralTeamLeader } from '../authUserProvider'
import { getLoginUrl, getRedirectUri } from '../helpers/authConfig'

function Auth() {
    const navigate = useNavigate()
    const [authorizing, setAuthorizing] = useState(false)
    const updateAuthUser = useUpdateAuthUserData()
    const setUserOrganisations = useSetUserOrganisations()
    const setTeamLeader = useSetPcpReferralTeamLeader()

    useEffect(() => {
        if (!authorizing) {
            setAuthorizing(true)
            Loading.circle('Authenticating...')
            startAuth()
        }
    }, [authorizing])

    const startAuth = async () => {
        try {
            const response = await getOauthTokens()
            const { id_token, access_token, refresh_token } = response

            window.localStorage.setItem('access', access_token)
            window.localStorage.setItem('id', id_token)
            window.localStorage.setItem('refresh', refresh_token)

            const user = await getOauthUser(access_token)
            window.localStorage.setItem('user', JSON.stringify(user))
            console.log('USER...', user)
            updateAuthUser(user)
            // `/init` returns `{ primary, organisations }` so the dashboard
            // knows which extra orgs (beyond the Cognito `custom:organisation`)
            // the user has been granted via `dashboard_user_org_memberships`.
            // Primary still drives the landing route below; the rest powers
            // the in-sidebar org switcher and multi-org route gating.
            const initResponse = await api({ url: `/init` })
            const initData = initResponse?.data || {}
            const primary = initData.primary || user['custom:organisation'] || ''
            const organisations: string[] = Array.isArray(initData.organisations) && initData.organisations.length > 0
                ? initData.organisations
                : [primary].filter(Boolean)
            // Mirror into localStorage so a page refresh — or a brand-new tab
            // opened via window.open (e.g. deep-linking into a client) — keeps
            // the org list available without re-running /init. localStorage is
            // shared across tabs (unlike sessionStorage), which is what lets
            // "open in new tab" land on gated routes instead of 404ing.
            // activeOrganisation is still reset to primary on every login via
            // setUserOrganisations, so per-session org switching is unaffected.
            window.localStorage.setItem(
                'userOrganisations',
                JSON.stringify({ primary, organisations })
            )
            setUserOrganisations({ primary, organisations })
            // PCP Referral team-leader status — drives the Team Leader stats page
            // + nav. Mirrored into localStorage so a refresh/new tab keeps it.
            const tl = initData.teamLeader || { isLeader: false, teamId: null, teamName: null }
            const teamLeader = {
                isLeader: Boolean(tl.isLeader),
                teamId: tl.team_id ?? tl.teamId ?? null,
                teamName: tl.team_name ?? tl.teamName ?? null,
            }
            window.localStorage.setItem('pcpReferralTeamLeader', JSON.stringify(teamLeader))
            setTeamLeader(teamLeader)
            Loading.remove()
            navigate(homeRoutes(primary))
        } catch (error: any) {
            // Always clear the spinner first — otherwise any failure (including
            // network/CORS errors, which have no `error.response`) leaves the
            // "Authenticating..." loader spinning forever.
            Loading.remove()
            console.log('AUTH ERROR: ', error?.response?.data || error?.message || error)

            if (error?.response?.status == 401) {
                Report.failure('Not Authorized', 'Please log in to use sentinel services.', 'Okay', () => {
                    window.location.href = getLoginUrl()
                })
            } else {
                // Covers invalid_grant, other API errors, and network/CORS
                // failures where no response object exists.
                Report.failure(
                    'An Error Occurred',
                    'An error occurred please log in again to continue.',
                    'Okay',
                    () => {
                        window.location.href = getLoginUrl()
                    }
                )
            }
        }
    }

    const getOauthUser = async (access_token: string) => {
        // console.log("------------ GET AUTH USER \n \n");
        const config = {
            headers: {
                Authorization: `Bearer ${access_token}`,
            },
        }
        const oAuthUserResponse = await axios.get(`${process.env.REACT_APP_OAUTH_USER}`, config)
        return oAuthUserResponse.data
    }

    const getOauthTokens = async () => {
        try {
            console.log('------------ GET AUTH TOKEN \n \n')
            const config = {
                headers: {
                    'content-type': 'application/x-www-form-urlencoded',
                    Authorization: `Basic ${process.env.REACT_APP_COGINTO_AUTHORIZATION}`,
                },
            }
            const search = window.location.search
            const urlParams = new URLSearchParams(search)
            const code = urlParams.get('code')
            const params = new URLSearchParams()
            params.append('code', `${code}`)
            params.append('grant_type', 'authorization_code')
            params.append('client_id', `${process.env.REACT_APP_COGNITO_CLIENT_ID}`)
            params.append('redirect_uri', getRedirectUri())
            const oAuthresponse = await axios.post(`${process.env.REACT_APP_OAUTH}`, params, config)

            return oAuthresponse.data
        } catch (error) {
            console.log('error getting OAUTH TOKEN: ', error)
            return false
        }
    }

    return <div className="auth"></div>
}

export const homeRoutes = (group: any) => {
    const homes = {
        Sentinel: '/home',
        PCPMO26: '/pcpmo26-home',
        FairClaim: '/fairclaim-home',
        'PCP Referral': '/pcp-referral/overview',
        'PCP Referral Engagement': '/pcp-referral/engagement',
        MBFCheck: '/mbfcheck-home',
        MFC: '/mfc-home',
        ADL: '/adl-home',
        MR: '/mail-referral-home',
        'Vision Launch': '/vision-launch-home',
        Sensible: '/re-engagement-overview',
        Score: '/allocate-claims',
        'Insurance Audit': '/insurance-audit',
        'Military Hearing Loss': '/military-hearing-loss/add-client',
        'Military Hearing Loss Admin': '/military-hearing-loss/lead-tracker',
        Gorilla: '/logout',
        Panaco: '/logout',
        Allegiant: '/logout',
    }
    return homes[group as keyof typeof homes]
}

export default Auth
